import type { Announcement } from "../services/AnnouncementService";

// Notification feed helpers: what happened, in plain words, without emoji
// (the app font can't draw them and they render as "?" boxes).

export type AlertKind = "price_drop" | "back_in_stock" | "top_deal" | "other";
export type AlertDay = "today" | "yesterday" | "week" | "older";

const EMOJI = /[\u{1F000}-\u{1FAFF}]|[\u{2600}-\u{27BF}]|[\u{2B00}-\u{2BFF}]|\u{FE0F}|\u{200D}|\u{20E3}/gu;

export const stripEmoji = (text: string) => text.replace(EMOJI, "").replace(/\s{2,}/g, " ").trim();

export function alertKind(alert: Announcement): AlertKind {
  const reason = alert.alertReason ?? "";
  if (reason === "xavier_deal") return "top_deal";
  if (reason.includes("back_in_stock")) return "back_in_stock";
  if (reason.includes("price_drop")) return "price_drop";
  return "other";
}

export function alertDiscountPercent(alert: Announcement): number {
  const oldPrice = alert.alertProduct?.oldPrice ?? 0;
  const newPrice = alert.alertProduct?.newPrice ?? 0;
  if (oldPrice <= 0 || newPrice <= 0 || newPrice >= oldPrice) return 0;
  return Math.round(((oldPrice - newPrice) / oldPrice) * 100);
}

/** Bold lead phrase for a feed row: "Down 75%", "Back in stock", "Top deal". */
export function alertHeadline(alert: Announcement, lang: "en" | "es"): string {
  const pct = alertDiscountPercent(alert);
  const kind = alertKind(alert);
  const down = pct > 0 ? (lang === "es" ? `Bajó ${pct}%` : `Down ${pct}%`) : (lang === "es" ? "Bajó de precio" : "Price drop");
  if (kind === "back_in_stock") return lang === "es" ? "De nuevo en stock" : "Back in stock";
  if (kind === "top_deal") return pct > 0 ? (lang === "es" ? `Oferta top · ${pct}% dto.` : `Top deal · ${pct}% off`) : (lang === "es" ? "Oferta top" : "Top deal");
  if (kind === "price_drop") return down;
  return pct > 0 ? down : stripEmoji(alert.title);
}

/** Short tag for alerts that aren't a plain price drop ("Back in stock", "Top deal"); null for price drops. */
export function alertTag(alert: Announcement, lang: "en" | "es"): string | null {
  const kind = alertKind(alert);
  if (kind === "back_in_stock") return lang === "es" ? "De nuevo en stock" : "Back in stock";
  if (kind === "top_deal") return lang === "es" ? "Oferta top" : "Top deal";
  return null;
}

/** The product the alert is about, falling back to the (emoji-free) title. */
export function alertSubject(alert: Announcement): string {
  return stripEmoji(alert.alertProduct?.name || alert.title || "");
}

export function alertDay(timestamp: number, now = Date.now()): AlertDay {
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  if (timestamp >= startOfToday) return "today";
  if (timestamp >= startOfToday - 86400000) return "yesterday";
  if (timestamp >= startOfToday - 6 * 86400000) return "week";
  return "older";
}

export const ALERT_DAY_LABELS: Record<AlertDay, { en: string; es: string }> = {
  today: { en: "Today", es: "Hoy" },
  yesterday: { en: "Yesterday", es: "Ayer" },
  week: { en: "This week", es: "Esta semana" },
  older: { en: "Earlier", es: "Anteriores" },
};

export type AlertFeedRow = { type: "day"; day: AlertDay } | { type: "alert"; alert: Announcement };

/** Newest first, with a day header before each group. */
export function buildAlertFeed(alerts: Announcement[], now = Date.now()): AlertFeedRow[] {
  const rows: AlertFeedRow[] = [];
  let current: AlertDay | null = null;
  for (const alert of [...alerts].sort((a, b) => b.createdAt - a.createdAt)) {
    const day = alertDay(alert.createdAt, now);
    if (day !== current) {
      current = day;
      rows.push({ type: "day", day });
    }
    rows.push({ type: "alert", alert });
  }
  return rows;
}

const URL_ONLY = /^\s*https?:\/\/\S+\s*$/;

/** Announcement text without emoji or stand-alone link lines (the link gets its own button). */
export function cleanAnnouncementBody(body: string, link?: string): string {
  return body
    .split(/\n{2,}/)
    .map((paragraph) =>
      paragraph
        .split("\n")
        .filter((line) => !URL_ONLY.test(line) && !(link && line.trim() === link.trim()))
        .map(stripEmoji)
        .join("\n")
        .trim()
    )
    .filter(Boolean)
    .join("\n\n");
}

export interface AnnouncementDeal {
  /** Product name from the body, without the trailing price line. */
  name: string | null;
  retailer: string | null;
  percentOff: number | null;
  price: number | null;
  wasPrice: number | null;
}

const PRICE_LINE = /\s*Now \$([\d,]+(?:\.\d+)?)\s*\(was \$([\d,]+(?:\.\d+)?)\)/i;
const toNumber = (value: string) => Number(value.replace(/,/g, ""));

/**
 * Deal announcements are posted as "93% off at Bath & Body Works Online" (or "64% off — $199.00")
 * with a body of "<product> Now $5.00 (was $69.95)". Anything else (feature news, notices) is null.
 */
export function parseAnnouncementDeal(announcement: Pick<Announcement, "title" | "body">): AnnouncementDeal | null {
  const title = stripEmoji(announcement.title ?? "");
  const body = stripEmoji(announcement.body ?? "");
  const pctMatch = title.match(/(\d{1,3})%\s*off/i);
  const priceMatch = body.match(PRICE_LINE) ?? title.match(PRICE_LINE);
  if (!pctMatch && !priceMatch) return null;

  const price = priceMatch ? toNumber(priceMatch[1]) : null;
  const wasPrice = priceMatch ? toNumber(priceMatch[2]) : null;
  let percentOff = pctMatch ? Number(pctMatch[1]) : null;
  if (percentOff === null && price !== null && wasPrice && wasPrice > price) {
    percentOff = Math.round(((wasPrice - price) / wasPrice) * 100);
  }

  const retailerMatch = title.match(/%\s*off at (.+?)(?:\s+online)?$/i);
  const name = priceMatch && body.match(PRICE_LINE) ? body.slice(0, body.search(PRICE_LINE)).replace(/(\.{3}|…)$/, "").trim() || null : null;

  return { name, retailer: retailerMatch ? retailerMatch[1].trim() : null, percentOff, price, wasPrice };
}

export const formatUsd = (value: number) => `$${value.toFixed(2)}`;
